/* eslint-disable react/jsx-curly-newline */
/* eslint-disable @typescript-eslint/prefer-optional-chain */
/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import React, { type ReactElement, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  ApplicationIcon,
  Box,
  Button,
  ButtonIcon,
  Chip,
  FooterActionBar,
  Icon,
  Link as DSLink,
  Loader,
  Menu,
  SidePanel,
  Typography,
} from '@saviynt/design-system';

import {
  SodApplicationGroup,
  SodFieldSet,
  SodRiskPriorityChip,
  SodSeverityChip,
  SodTextFieldSet,
} from '../../../../../../../shared/components';
import {
  CF_NEU_STL,
  CF_SEC_BLD,
  ENTITLEMENT_DETAILS_TABS,
  testIds,
  violationsEntitlement,
} from '../../../../../../../shared/constants';
import {
  useInvertedLocalize,
  useNavigateTo,
  useTypedSelector,
} from '../../../../../../../shared/hooks';
import { type EntKebabOptions } from '../../../../../../ViolationsEntitlement/data-store';
import { type EntitlementRisksSidePanelGroupRowContextState } from '../../../../../data-layer/detailsTypes';
import { detailsActions } from '../../../../../data-store/detailsSlice';
import { type RiskItem, useGetInherentRisksMutation } from '../../data-layer';

import './InherentRisksSidePanel.css';

interface InherentRisksSidePanelProps {
  entKeys: number[];
}

/**
 * InherentRisksSidePanel Component
 *
 * Detailed sidepanel view for individual inherent risk records with comprehensive information
 * display, navigation capabilities, and quick access to related data and actions for
 * risk management and analysis.
 *
 * @component
 * @returns {React.ReactElement} Sidepanel with detailed inherent risk information including:
 *   - Risk metadata and descriptions
 *   - Quick navigation links to violating entitlements and violations
 *   - Risk score and priority indicators with color-coded chips
 *   - Application information with logos
 *   - Conflicting job functions display
 *   - Custom properties and risk owner information
 *
 * @example
 * <InherentRisksSidePanel entKeys={[123]} />
 *
 * @dependencies
 * - Redux: isSidePanelVisible, rowContext from state.aag.details.entitlementRisks.sidepanel
 * - Hooks: useInvertedLocalize, useNavigateTo
 * - Components: SidePanel, SodFieldSet, SodSeverityChip, TextFieldSet, TextFieldValue
 */
function InherentRisksSidePanel({
  entKeys,
}: InherentRisksSidePanelProps): ReactElement {
  const l = useInvertedLocalize();
  const dispatch = useDispatch();
  const [getInherentRisks, { isLoading }] = useGetInherentRisksMutation();
  const [riskData, setRiskData] = useState<RiskItem | null>(null);
  const initialKebabOptionsWithIcons: EntKebabOptions[] = [
    {
      label: l('View Violations'),
      value: ENTITLEMENT_DETAILS_TABS.TAB_VIOLATIONS,
      icon: 'SOD',
    },
  ];
  const {
    entitlementRisks: {
      sidepanel: { isSidePanelVisible, rowContext },
    },
    entitlementSummary: { selectedEntitlement, data: selectedEntitlementData },
  } = useTypedSelector((state) => state.aag.details);

  const entitlementDisplayName =
    selectedEntitlement?.entitlementDisplayName ??
    selectedEntitlementData?.entitlementDisplayName;

  const sidepanelRef = useRef(null);
  const { navigateToDetails } = useNavigateTo();

  // Get selected risk ID from rowContext and fetch fresh data
  useEffect(() => {
    const selectedRiskId = rowContext.current && rowContext.current.riskId;

    if (selectedRiskId && isSidePanelVisible) {
      getInherentRisks({
        includeDetails: true,
        includeViolationSummary: true,
        riskKeys: [selectedRiskId],
        entitlementKeys: entKeys,
        businessProcesses: [],
        rulesets: [],
        riskNames: [],
        riskTypes: [],
        statuses: [],
        priorities: [],
        functionKeys: [],
        columnToSort: 'riskName',
        globalSearch: '',
        max: 1,
        offset: 0,
        order: 'ASC',
      })
        .unwrap()
        .then((response) => {
          if (response.data && response.data.length > 0) {
            setRiskData(response.data[0]);
          } else {
            setRiskData(rowContext.current as RiskItem | null);
          }
        })
        .catch(() => {
          // Fallback to rowContext data if API fails
          setRiskData(rowContext.current as RiskItem | null);
        });
    } else {
      setRiskData(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowContext, isSidePanelVisible, getInherentRisks]);

  // Clear riskData when side panel is not visible
  useEffect(() => {
    if (!isSidePanelVisible) {
      setRiskData(null);
    }
  }, [isSidePanelVisible]);

  const handleSidePanelClose = () => {
    setRiskData(null);
    dispatch(
      detailsActions.setEntitlementRisksSidePanelRowContext({
        isSidePanelVisible: false,
        rowContext: {} as EntitlementRisksSidePanelGroupRowContextState,
      })
    );
  };

  const handleSourceChange = (
    sourceType: string,
    activeTab: string,
    item: RiskItem | null
  ) => {
    dispatch(detailsActions.setSelectedEntitlementRisks(item));
    const isViolationsTab =
      activeTab === ENTITLEMENT_DETAILS_TABS.TAB_VIOLATIONS;

    navigateToDetails({
      key: String(item?.riskId),
      activeTab,
      isEntitlementRoute: true,
      navigation: {
        source: sourceType,
        title: item?.riskName ?? '',
        ...(isViolationsTab
          ? {
              entName: entitlementDisplayName ?? '',
              riskName: item?.riskName ?? '',
            }
          : {}),
      },
    });
  };

  const sodSidePanelLayout = (item: RiskItem | null) => {
    const functionKeys = item
      ? [
          item.function1,
          item.function2,
          item.function3,
          item.function4,
          item.function5,
        ].filter(Boolean)
      : [];
    const violationEntitlementCount =
      item?.riskViolationSummaryObjects?.violationEntitlementCount ?? 0;
    const violationCount =
      item?.riskViolationSummaryObjects?.violationCount ?? 0;
    const hasViolations = violationEntitlementCount > 0 || violationCount > 0;
    const isCriticalPriority = Boolean(
      item?.priority && String(item.priority).toLowerCase().includes('critical')
    );

    return (
      <>
        {/* 3 Col Link Grid */}
        {hasViolations && (
          <Box className='InherentRisksSidePanel-detailLinks'>
            {violationEntitlementCount > 0 && (
              <Box className='InherentRisksSidePanel-detailLinkBlock'>
                <Typography kind='body3' color={CF_NEU_STL}>
                  {l('Violating Entitlements')}
                </Typography>
                <DSLink
                  kind='button'
                  onClick={() => {
                    if (item) {
                      handleSourceChange(
                        violationsEntitlement,
                        ENTITLEMENT_DETAILS_TABS.TAB_RISKS_CONFLICTS,
                        item
                      );
                    }
                  }}
                  text={String(violationEntitlementCount)}
                  size='large'
                  prefixIconKind='Entitlement'
                  suffixIconKind='ArrowRight'
                />
              </Box>
            )}
            {violationCount > 0 && (
              <Box className='InherentRisksSidePanel-detailLinkBlock'>
                <Typography kind='body3' color={CF_NEU_STL}>
                  {l('Violations')}
                </Typography>
                <DSLink
                  kind='button'
                  onClick={() => {
                    handleSourceChange(
                      violationsEntitlement,
                      ENTITLEMENT_DETAILS_TABS.TAB_VIOLATIONS,
                      item
                    );
                  }}
                  text={String(violationCount)}
                  size='large'
                  prefixIconKind='SOD'
                  suffixIconKind='ArrowRight'
                />
              </Box>
            )}
          </Box>
        )}
        {/* Conflicting Functions */}
        {functionKeys.length > 0 && (
          <Box className='InherentRisksSidePanel-detailLinks'>
            <SodFieldSet
              label={l('Conflicting Job Functions')}
              value={
                <Box className='InherentRisksSidePanel-detailLinks-functions'>
                  {functionKeys.map((functionValue) => (
                    <Box
                      key={functionValue}
                      className='InherentRisksSidePanel-detailLinks-functions-item'>
                      <Chip
                        label=''
                        color='warning'
                        kind='subtleFilled'
                        size='xSmall'
                        leftIcon={<Icon kind='JobFunction' />}
                      />
                      <Typography
                        kind='body2-bold'
                        color={CF_SEC_BLD}
                        lineLimit={1}>
                        {functionValue}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              }
            />
          </Box>
        )}
        {/* Field Sets */}
        <Box className='InherentRisksSidePanel-contentBody'>
          <SodFieldSet
            label={l('Risk Score')}
            value={
              <>
                {item?.riskScore &&
                item?.riskScore !== 0 &&
                item.riskSeverity ? (
                  <SodRiskPriorityChip
                    key='SodRiskPriorityChip'
                    priority={String(item.riskSeverity)}
                    riskScore={
                      item?.riskScore ??
                      item?.riskViolationSummaryObjects?.riskScore ??
                      null
                    }
                    isPrefixInterpoint={false}
                  />
                ) : (
                  <Typography kind='body2' color={CF_SEC_BLD}>
                    {item?.riskScore ??
                      item?.riskViolationSummaryObjects?.riskScore}
                  </Typography>
                )}
              </>
            }
          />
          <SodFieldSet
            label={l('Application')}
            value={
              item?.endpointName ? (
                <Box
                  className='Sod-EntitlementSummary-rowItem'
                  id='Sod-EntitlementSummary-rowItem-application'>
                  <ApplicationIcon logo={item.endpointName} size='xxSmall' />
                  <Typography kind='body2' color={CF_SEC_BLD}>
                    {l(item.endpointName)}
                  </Typography>
                </Box>
              ) : (
                <Box
                  className='Sod-EntitlementSummary-rowItem'
                  id='Sod-EntitlementSummary-rowItem-application'>
                  <SodApplicationGroup
                    label={
                      (selectedEntitlement?.endpointName ||
                        selectedEntitlementData?.endpointName) ??
                      '-'
                    }
                    firstLogo={
                      (selectedEntitlement?.endPointLogo ||
                        selectedEntitlementData?.endPointLogo) ??
                      ''
                    }
                  />
                </Box>
              )
            }
          />
          <SodTextFieldSet
            label={l('Risk Description')}
            value={item?.description}
            lineLimit={1}
          />
          <SodTextFieldSet label={l('Ruleset')} value={item?.ruleset} />
          <SodTextFieldSet
            label={l('Business Process')}
            value={item?.businessProcess}
          />
          <SodFieldSet
            label={l('Risk Priority')}
            value={
              isCriticalPriority ? (
                <Chip
                  size='xSmall'
                  kind='subtleFilled'
                  label={l('Critical')}
                  color='pink'
                />
              ) : (
                <SodSeverityChip text={String(item?.priority ?? 'None')} />
              )
            }
          />
          <SodTextFieldSet label={l('Risk Type')} value={item?.riskType} />
          <SodTextFieldSet label={l('Risk Owner')} value={item?.riskOwner} />
          <SodTextFieldSet
            label={l('Custom Property 1')}
            value={item?.customProperty1}
          />
          <SodTextFieldSet
            label={l('Custom Property 2')}
            value={item?.customProperty2}
          />
        </Box>
      </>
    );
  };

  const sodSidePanelFooter = (item: RiskItem | null) => (
    <FooterActionBar className='InherentRisksSidePanel-footer'>
      <Button
        type='button'
        kind='filled'
        size='medium'
        isDisabled={isLoading}
        onClick={
          () =>
            item &&
            handleSourceChange(
              violationsEntitlement,
              ENTITLEMENT_DETAILS_TABS.TAB_RISKS_SUMMARY,
              item
            )
          // eslint-disable-next-line react/jsx-curly-newline
        }>
        {l('View Details')}
      </Button>
      <Menu
        options={initialKebabOptionsWithIcons}
        onChange={(option: EntKebabOptions) => {
          if (option.value === ENTITLEMENT_DETAILS_TABS.TAB_VIOLATIONS) {
            handleSourceChange(
              violationsEntitlement,
              ENTITLEMENT_DETAILS_TABS.TAB_VIOLATIONS,
              item
            );
          }
        }}
        trigger={
          <ButtonIcon
            size='medium'
            kind='ghost'
            ariaLabel={l('more')}
            isDisabled={isLoading}
            icon={<Icon kind='MoreVertical' />}
          />
        }
        placement='top-end'
      />
    </FooterActionBar>
  );

  return (
    <Box className='InherentRisksSidePanel-sidePanelWrapper'>
      <SidePanel
        ref={sidepanelRef}
        isOpen={isSidePanelVisible}
        onClose={handleSidePanelClose}
        header={{
          title: riskData?.riskName ?? '',
          description: `${l('Inherent Risk for')} ${
            entitlementDisplayName ?? ''
          }`,
          icon: 'CriticalHighRiskHexagon',
        }}
        appIconSize='medium'
        layout={
          !isLoading ? (
            sodSidePanelLayout(riskData)
          ) : (
            <Box className='Sod-Violations-loaderWrapper'>
              <Loader kind='dots' format='inline' color='brand' />
            </Box>
          )
        }
        footer={sodSidePanelFooter(riskData)}
        isSidePanelNavigatingList
        isPreviousDisabled
        isNextDisabled
        isMobileWidth={false}
        isTabletWidth={false}
        className='InherentRisksSidePanel-sidePanel'
        classNames={{
          layoutClassName: 'InherentRisksSidePanel-sidePanelLayout',
          headerClassName: 'InherentRisksSidePanel-sidePanelHeader',
          innerContentClassName: 'InherentRisksSidePanel-sidePanelInnerContent',
          footerClassName: 'InherentRisksSidePanel-sidePanelFooter',
        }}
        dataTestId={testIds.SOD_ENTITLEMENT_TAB_RISKS_SIDE_PANEL}
      />
    </Box>
  );
}

export default InherentRisksSidePanel;
